// =============================================================================
//
// Prebid constants
//
// hb_ prefix are key value that should be auto-populated by prebid.js
//
// =============================================================================

import { type MediaTypes } from '@/lib/ads/types/adConfig'

type BidCpmAdjustment = Record<string, { bidCpmAdjustment: (bidCpm: number) => number }>

type AdserverTargeting = {
  key: string
  val: (bid: Record<string, string | number>) => string | number
}

type BidderSettings = {
  standard: {
    adserverTargeting: AdserverTargeting[]
  }
} & Record<string, object>

export type Pbjs = { bidderSettings: BidderSettings; version: string } & BidCpmAdjustment

declare global {
  // Prebid attaches itself to the window object as pbjs
  interface Window {
    pbjs: Pbjs
  }
}

/**
 * each bidder has their own unique params which can vary in type
 * so best to keep the type set as unknown
 * */
export type BidConfig = {
  bidder: string
  params: Record<string, unknown>
  responsiveRules: Record<string, never>
  labelAny?: string[] // for SRP (comes from GOT)
  labels?: string[] // for homepage (comes from legacy config)
}

export type PrebidConfig = {
  mediaTypes: MediaTypes
  bids?: BidConfig[]
}

export const PREBID_KEYS = {
  PREBID_AUCTION_PRICE: 'hb_pb',
  /** Name of the bidding partner */
  PREBID_BIDDING_PARTNER: 'hb_bidder',
  /** Size of the ad width and height (ex: 300x250) */
  PREBID_AD_SIZE: 'hb_size',
  /** Ad Id */
  PREBID_AD_ID: 'hb_adid',
  PREBID_DEAL_ID: 'hb_deal',
  PREBID_TIME_TO_RESPOND: 'hb_bid_time',
  PREBID_AD_SOURCE: 'hb_source',
  /** Format of the ad (ex: banner) */
  PREBID_AD_FORMAT: 'hb_format',
}

/**
 * Values for initializing prebid custom config.
 * Based on the production values from banners.js
 * in box-canada-webapp-static
 */
export const PREBID_CONFIG = {
  BRACKET_PENNIES: 10.0, // 10 dollars
  BRACKET_NICKELS: 25.0, // 25 dollars
  PENNIES_PER_$: 100,
  NICKELS_PER_$: 20,
  MAX_BUCKET: '101300',
}
